import { config } from "@/config";
import Table from "@commons/Table/TableContent";
import { useGetIndividualAdditionalData } from "@services/individuals/additionalData/list";
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { formatDateTime } from "@utils/formatters";
import { usePage } from "@hooks/usePage";
import ModalViewAdditionalData from "./ModalViewAdditionalData";
import { EventAdditionalDataColumns } from "@typings/entities/individuals/additionalData/table";
import type { IndividualAdditionalData } from "@typings/entities/individuals/additionalData";

function prepareAdditionalData(
  items: IndividualAdditionalData[],
): IndividualAdditionalData[] {
  return items.map((item) => ({
    ...item,
    createdAt: formatDateTime(item.createdAt) || "",
  }));
}

function AdditionalDataTable({ individualId }: { individualId: string }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const [selectedAdditionalDataId, setSelectedAdditionalDataId] = useState<
    string | null
  >(null);
  const { openModal } = usePage();

  const {
    data: additionalData,
    isLoading,
    error,
  } = useGetIndividualAdditionalData(individualId, {
    page: page + 1,
    limit: rowsPerPage,
  });

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar dados adicionais. Tente novamente.");
    }
  }, [error]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const handleViewAdditionalData = (row: IndividualAdditionalData) => {
    if (row?.id) {
      setSelectedAdditionalDataId(row.id);
      openModal("viewAdditionalData");
    }
  };

  const heads = EventAdditionalDataColumns;
  const processedData = prepareAdditionalData(additionalData?.items ?? []);

  if (error) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center py-8">
        <ErrorOutlineIcon className="text-red-500 mb-2 text-6xl" />
        <span className="text-red-600 text-center">
          Ocorreu um erro ao carregar os dados adicionais.
        </span>
      </div>
    );
  }

  if (!additionalData) return null;

  return (
    <>
      <Table.Root isLoading={isLoading}>
        <Table.Head heads={heads} actions={true} />
        <Table.Body
          rows={processedData}
          columns={heads.map((head) => head.id)}
          actions={["view"]}
          onView={handleViewAdditionalData}
        />
        <Table.Footer
          colSpan={heads.length}
          count={additionalData?.meta.totalItems ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Table.Root>

      <ModalViewAdditionalData
        individualId={individualId}
        additionalDataId={selectedAdditionalDataId}
      />
    </>
  );
}

export default AdditionalDataTable;
